import { Trans } from "@lingui/react/macro";
import styles from "./Patients.module.scss";
import { Outlet, useNavigate } from "react-router";
import CalendarDayIcon from "@medoma/components/icons/CalendarDayIcon";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import { PlainButton } from "@medoma/components/Button/Button";
import { Tab, TabList, TabPanel } from "@medoma/components/Tabs";
import {
  getPatientFullName,
  type IPatientName,
  patientStatusDictionary,
  patientStatusSchema,
} from "@medoma/models/patients";
import { useRoomNamesWithUnreadNotifications } from "@medoma/chat/useUnreadNotificationCounts";
import {
  patientsWithUnhandledMeasurementsQueryOptions,
  dischargedPatientsQueryOptions,
  usePatients,
} from "@/api/Patients";
import { useQuery } from "@tanstack/react-query";
import UnreadChatMessagesIndicator from "@/components/IconWithIndicator/UnreadChatMessagesIndicator/UnreadChatMessagesIndicator";
import UnhandledMeasurementsIndicator from "@/components/IconWithIndicator/UnhandledMeasurementsIndicator/UnhandledMeasurementsIndicator";
import { useLingui } from "@lingui/react/macro";
import { PageToolbar } from "@/components/PageToolbar/PageToolbar";
import { filterByNameTerms } from "@medoma/components/SearchField/searchUtils";
import { usePatientNameFilter } from "./PatientsContext";
import { PatientsContextProvider } from "./PatientsContextComponents";
import { PatientsPatientNameFilterInput } from "./PatientsPatientNameFilterInput";
import { PatientsFilterBannerSection } from "./PatientsFilterBannerSection";

const formatTabCount = (filtered: number | undefined, total: number) =>
  filtered === undefined ? `(${total})` : `(${filtered}/${total})`;

const Header = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { value: patientNameFilter } = usePatientNameFilter();
  const isFiltering = patientNameFilter !== "";

  // One combined fetch covers prospect + admitted + paused counts (paused
  // patients have admitted status with a pausedAt timestamp). Discharged is
  // deferred — it only fetches when the user navigates into that tab (the
  // tab populates the shared query cache) or starts a search, since the
  // discharged list can become very heavy.
  const { data: patients } = usePatients({
    statuses: [
      patientStatusSchema.enum.prospect,
      patientStatusSchema.enum.admitted,
    ],
  });
  const { data: dischargedPatients } = useQuery({
    ...dischargedPatientsQueryOptions(),
    enabled: isFiltering,
  });

  const prospectPatients = patients?.filter(
    ({ status }) => status === patientStatusSchema.enum.prospect,
  );
  const admittedPatients = patients?.filter(
    (patient) =>
      patient.status === patientStatusSchema.enum.admitted && !patient.pausedAt,
  );
  const pausedPatients = patients?.filter(
    (patient) =>
      patient.status === patientStatusSchema.enum.admitted && patient.pausedAt,
  );

  const countMatching = (list: { name: IPatientName }[] | undefined) =>
    isFiltering
      ? list?.filter((patient) =>
          filterByNameTerms(
            getPatientFullName(patient.name),
            patientNameFilter,
          ),
        ).length
      : undefined;

  const filteredProspectCount = countMatching(prospectPatients);
  const filteredAdmittedCount = countMatching(admittedPatients);
  const filteredPausedCount = countMatching(pausedPatients);
  const filteredDischargedCount = countMatching(dischargedPatients);

  const roomIdsWithUnreadNotifications = useRoomNamesWithUnreadNotifications();
  const { data: patientsWithUnhandledMeasurements } = useQuery(
    patientsWithUnhandledMeasurementsQueryOptions,
  );

  const someProspectPatientHasUnreadChat =
    prospectPatients?.some((patient) =>
      roomIdsWithUnreadNotifications?.some((id) => id === patient.id),
    ) ?? false;
  const someAdmittedPatientHasUnreadChat =
    admittedPatients?.some((patient) =>
      roomIdsWithUnreadNotifications?.some((id) => id === patient.id),
    ) ?? false;
  const somePausedPatientHasUnreadChat =
    pausedPatients?.some((patient) =>
      roomIdsWithUnreadNotifications?.some((id) => id === patient.id),
    ) ?? false;

  const someProspectPatientHasUnhandledMeasurements =
    prospectPatients?.some((patient) =>
      patientsWithUnhandledMeasurements?.some(
        ({ id, unhandledMeasurementsCount }) =>
          id === patient.id && unhandledMeasurementsCount > 0,
      ),
    ) ?? false;
  const someAdmittedPatientHasUnhandledMeasurements =
    admittedPatients?.some((patient) =>
      patientsWithUnhandledMeasurements?.some(
        ({ id, unhandledMeasurementsCount }) =>
          id === patient.id && unhandledMeasurementsCount > 0,
      ),
    ) ?? false;
  const somePausedPatientHasUnhandledMeasurements =
    pausedPatients?.some((patient) =>
      patientsWithUnhandledMeasurements?.some(
        ({ id, unhandledMeasurementsCount }) =>
          id === patient.id && unhandledMeasurementsCount > 0,
      ),
    ) ?? false;

  return (
    <header>
      <PageToolbar showDaySwitcher={false}>
        <div className={styles.tools}>
          <PatientsPatientNameFilterInput />
          <PlainButton onClick={() => navigate(`register`)}>
            <PlusIcon />
            <Trans>Registrera patient</Trans>
          </PlainButton>

          <PlainButton onClick={() => navigate(`daily-overview`)}>
            <CalendarDayIcon />
            <Trans>Visa dagsöversikt</Trans>
          </PlainButton>
        </div>
      </PageToolbar>
      <PatientsFilterBannerSection />

      <div className={styles.tabs}>
        <TabList>
          <Tab to="prospect">
            {t(patientStatusDictionary.prospect.plural)}
            {prospectPatients
              ? ` ${formatTabCount(filteredProspectCount, prospectPatients.length)}`
              : ""}
            <UnreadChatMessagesIndicator
              hasUnreadMessages={someProspectPatientHasUnreadChat}
            />
            <UnhandledMeasurementsIndicator
              hasUnhandledMeasurements={
                someProspectPatientHasUnhandledMeasurements
              }
            />
          </Tab>
          <Tab to="admitted">
            {t(patientStatusDictionary.admitted.plural)}
            {admittedPatients
              ? ` ${formatTabCount(filteredAdmittedCount, admittedPatients.length)}`
              : ""}
            <UnreadChatMessagesIndicator
              hasUnreadMessages={someAdmittedPatientHasUnreadChat}
            />
            <UnhandledMeasurementsIndicator
              hasUnhandledMeasurements={
                someAdmittedPatientHasUnhandledMeasurements
              }
            />
          </Tab>
          {pausedPatients && pausedPatients.length > 0 ? (
            <Tab to="paused">
              <Trans>Pausade</Trans>{" "}
              {formatTabCount(filteredPausedCount, pausedPatients.length)}
              <UnreadChatMessagesIndicator
                hasUnreadMessages={somePausedPatientHasUnreadChat}
              />
              <UnhandledMeasurementsIndicator
                hasUnhandledMeasurements={
                  somePausedPatientHasUnhandledMeasurements
                }
              />
            </Tab>
          ) : (
            <></>
          )}
          <Tab to="discharged">
            {t(patientStatusDictionary.discharged.plural)}
            {dischargedPatients
              ? ` ${formatTabCount(filteredDischargedCount, dischargedPatients.length)}`
              : ""}
          </Tab>
          <Tab to="all">
            <Trans>Alla</Trans>
          </Tab>
        </TabList>
      </div>
    </header>
  );
};

const Patients = () => {
  return (
    <PatientsContextProvider>
      <Header />
      <TabPanel>
        <Outlet />
      </TabPanel>
    </PatientsContextProvider>
  );
};

export default Patients;
